'use client'

import { useState } from 'react'
import { useSettingsStore } from '../stores/settingsStore'
import { getCurrentGeneratorModel } from '../lib/generatorModelSelection'

interface SectionOutlineInput {
  chapterTitle: string
  chapterNumber: number
  sectionNumber: number
  totalSections: number
  targetLength: number
  plotContext?: string
  previousSectionSummaries?: Array<{
    sectionNumber: number
    summary: string
  }>
  previousSectionText?: string  // 前のセクションの実際のテキスト
  worldContext?: string
  genre?: string
  writingStyle?: string
  useProMode?: boolean
  uploadedContent?: string  // アップロードされた参考資料
}

export function useSectionOutlineGenerator() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { activeApiKey } = useSettingsStore()

  const generateOutline = async (input: SectionOutlineInput): Promise<string> => {
    setIsGenerating(true)
    setError(null)

    const apiKey = activeApiKey
    if (!apiKey) {
      setError('APIキーが設定されていません')
      setIsGenerating(false)
      throw new Error('APIキーが設定されていません')
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
      const response = await fetch(`${apiUrl}/api/v1/generate/section-outline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey.key,
        },
        body: JSON.stringify({
          ...input,
          modelId: getCurrentGeneratorModel('scene')
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('セクションあらすじ生成エラー:', error)
        
        // APIキーのエラーを明示
        if (error.message?.includes('APIキー')) {
          throw new Error('APIキーが無効です。設定画面からAPIキーを確認してください。')
        }
        
        throw new Error(error.message || 'あらすじ生成に失敗しました')
      }

      const data = await response.json()
      setIsGenerating(false)
      return data.result.summary
    } catch (err: any) {
      setError(err.message)
      setIsGenerating(false)
      throw err
    }
  }

  const generateAllOutlines = async (
    chapterTitle: string,
    chapterNumber: number,
    totalSections: number,
    sectionTargetLength: number,
    plotContext?: string,
    worldContext?: string,
    genre?: string,
    writingStyle?: string,
    useProMode?: boolean,
    uploadedContent?: string
  ): Promise<Array<{ sectionNumber: number; summary: string }>> => {
    setIsGenerating(true)
    setError(null)

    const apiKey = activeApiKey
    if (!apiKey) {
      setError('APIキーが設定されていません')
      setIsGenerating(false)
      throw new Error('APIキーが設定されていません')
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
      const response = await fetch(`${apiUrl}/api/v1/generate/section-outlines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey.key,
        },
        body: JSON.stringify({
          chapterTitle,
          chapterNumber,
          totalSections,
          sectionTargetLength,
          plotContext,
          worldContext,
          genre,
          writingStyle,
          useProMode,
          uploadedContent,
          modelId: getCurrentGeneratorModel('scene')
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        console.error('全セクションあらすじ生成エラー:', error)
        
        throw new Error(error.message || '全セクションあらすじ生成に失敗しました')
      }

      const data = await response.json()
      setIsGenerating(false)
      return data.result.outlines
    } catch (err: any) {
      setError(err.message)
      setIsGenerating(false)
      throw err
    }
  }

  return {
    generateOutline,
    generateAllOutlines,
    isGenerating,
    error
  }
}
